/* eslint-disable jsx-a11y/anchor-is-valid */
import "./StripePaymentPage.scss";

import { Alert, Button, ExclamationTriangleIcon, Flex, Text } from "@fluentui/react-northstar";
import FormState, { FormData } from "@shopify/react-form-state";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { StripeCardElement, StripeCardElementOptions, StripeError } from "@stripe/stripe-js";
import { StandardPlanLevels } from "@teams/types";
import { CouponDTO, UserDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { StripeForm } from "../../_shared/components/Checkout/StripeForm";
import { ContactUsLink } from "../../_shared/components/ContactUsLink";
import { FieldLabel } from "../../_shared/components/fields";
import { TextInputField } from "../../_shared/components/fields/TextInputField";
import { PollyIcon } from "../../_shared/components/PollyIcon";
import { FetchError, FetchResult, isError } from "../../_shared/services/ChannelApi";
import { paymentsApi } from "../../_shared/services/PaymentsApi";
import { useTeamsContext } from "../../_shared/state/TeamsContextProvider";
import { useTheme } from "../../_shared/state/ThemeProvider";
import { clientEventTracker } from "../../_shared/utils/clientTelemetry";
import {
  PaymentInput,
  getPaymentValidator,
  getPlanType,
  getStripeCardOptions,
} from "../../_shared/utils/paymentHelpers";

type CorePaymentInput = Pick<PaymentInput, "name" | "email" | "licenses" | "planId">;
const CYBER_MONDAY_COUPON = "CYBERMONDAY30";
type props = {
  selectedPlan: StandardPlanLevels;
  user: UserDTO;
  isTrial: boolean;
  authToken?: string;
  isDarkTheme: boolean;
  classForDarkTheme: string;
  onCheckoutComplete: (success: boolean, trialEnd?: Date) => void;
};

export const StripePaymentPage: React.FC<props> = props => {
  return (
    <StripeForm>
      <StripePaymentForm {...props} />
    </StripeForm>
  );
};

const StripePaymentForm: React.FC<props> = ({
  selectedPlan,
  user,
  isTrial,
  authToken,
  classForDarkTheme,
  onCheckoutComplete,
}) => {
  const { teamsContext } = useTeamsContext();
  const { siteVariables } = useTheme();
  const elements = useElements();
  const stripe = useStripe();

  const [noOfLicenses, setNoOfLicenses] = useState<number>(1);
  const [stripeError, setStripeError] = useState<StripeError>();
  const [genericError, setGenericError] = useState<Error>();
  const [enableSubmit, setEnableSubmit] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<string>();
  const [appliedCouponCode, setAppliedCouponCode] = useState<CouponDTO>();

  const initialValues = {
    planId: selectedPlan.id,
    name: user.name ?? "",
    email: user.upn ?? "",
    licenses: 1,
    coupon: user.featureFlags.cyberMonday2023
      ? {
          percentOff: 30,
          id: CYBER_MONDAY_COUPON,
          name: "Cyber Monday 2023",
        }
      : {},
  } as CorePaymentInput;

  useEffect(() => {
    if (user.featureFlags.cyberMonday2023) {
      setCouponCode(CYBER_MONDAY_COUPON);
      setAppliedCouponCode({
        id: CYBER_MONDAY_COUPON,
        name: "Cyber Monday 2023",
        percentOff: 30,
        amountOff: 0,
      });
    }
  }, [user.featureFlags.cyberMonday2023]);

  const planType = useMemo(() => {
    return getPlanType(selectedPlan.id);
  }, [selectedPlan.id]);

  const cardOptions = useMemo<StripeCardElementOptions>(() => {
    return getStripeCardOptions(siteVariables);
  }, [siteVariables]);

  const totalPrice = useMemo(() => {
    if (planType === "Standard") {
      return selectedPlan.yearlyPrice;
    }
    if (planType === "Pro") {
      return noOfLicenses * selectedPlan.yearlyPrice;
    }
  }, [noOfLicenses, planType, selectedPlan.yearlyPrice]);

  const [renewalFrequencyText, nextPaymentDate] = useMemo(() => {
    switch (selectedPlan.paymentFrequency) {
      case "monthly":
        return ["month", moment().add(1, "months").format("LL")];
      case "quarterly":
        return ["3 months", moment().add(3, "months").format("LL")];
      case "semi_annually":
        return ["6 months", moment().add(6, "months").format("LL")];
      case "yearly":
        return ["12 months", moment().add(12, "months").format("LL")];
      default:
        return ["", ""];
    }
  }, [selectedPlan.paymentFrequency]);

  const checkoutSubmit = useCallback(
    async (formDetails: FormData<CorePaymentInput>) => {
      if (!elements || !stripe) {
        setGenericError(new Error("stripe has not loaded yet"));
        return;
      }

      setGenericError(undefined);
      setStripeError(undefined);

      const { fields } = formDetails;

      const cardElement = elements.getElement(CardElement);

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement as StripeCardElement,
        billing_details: {
          name: fields.name.value,
          email: fields.email.value,
        },
      });

      if (error || !paymentMethod) {
        setStripeError(error);
        clientEventTracker.track("stripe/payment/error", user, {
          error,
          user: fields.email.value,
          pricingPlan: selectedPlan,
          url: window.location.pathname,
        });
        return;
      }

      let fetchResult: FetchResult<{ id: string; trialEnd?: string; message?: string }>;

      fetchResult = await paymentsApi.createStripeSubscription(authToken ?? teamsContext, {
        planId: fields.planId.value,
        licenseQuantity: noOfLicenses,
        paymentId: paymentMethod.id,
        name: fields.name.value,
        email: fields.email.value,
        billingFrequency: selectedPlan.paymentFrequency,
        isTrial: isTrial,
        coupon: couponCode,
      });

      if (isError(fetchResult)) {
        console.error(fetchResult);
        if (fetchResult instanceof FetchError && fetchResult.statusCode === 401) {
          setGenericError(
            new Error(
              "Your session has expired. Please refresh and authenticate again to continue."
            )
          );
          clientEventTracker.trackFatalError("checkout error", user, {
            ...fetchResult,
            failureReason: "Session expired",
          });
        } else {
          setGenericError(fetchResult);
          clientEventTracker.trackFatalError("checkout error", user, {
            fetchResult,
          });
        }
      } else if (fetchResult.message) {
        console.error(fetchResult.message);
        setGenericError(new Error(fetchResult.message));
        clientEventTracker.trackFatalError("checkout error", user, {
          fetchResult,
        });
      } else {
        let trialEnd: Date | undefined;
        if (fetchResult.trialEnd) {
          // substract a day because we add an additional day to trial period while creating subscription
          // to cater for time zone differneces and refund requests. No need to inform user about the additional day.
          // https://subcurrent.atlassian.net/browse/TEAMS-1378
          trialEnd = moment(fetchResult.trialEnd).add(-1, "days").toDate();
        }
        onCheckoutComplete(true, trialEnd);
      }
    },
    [
      couponCode,
      authToken,
      elements,
      isTrial,
      noOfLicenses,
      onCheckoutComplete,
      selectedPlan,
      stripe,
      teamsContext,
      user,
    ]
  );

  return (
    <Flex column fill gap="gap.large" className="payment-page-container">
      <Flex hAlign="center" gap="gap.small" space="around" className="payment-page-inner-container">
        <Flex column gap="gap.large" className="payment-container">
          <FormState
            validateOnSubmit
            initialValues={initialValues as PaymentInput}
            onSubmit={checkoutSubmit}
            validators={getPaymentValidator()}
          >
            {({ fields, submitting, submit }) => {
              return (
                <>
                  <TextInputField
                    field={fields.name}
                    label="Name"
                    labelProps={{ weight: "bold", styles: { marginBottom: "20px" } }}
                    defaultValue={user?.name}
                    required
                    fluid
                    autoFocus
                    disabled={submitting}
                    className={`stripe-input-field${classForDarkTheme}`}
                  />

                  <TextInputField
                    field={fields.email}
                    label="Billing Email"
                    required
                    fluid
                    disabled={submitting}
                    className={`stripe-input-field${classForDarkTheme}`}
                  />

                  <div className={`stripe-input-field${classForDarkTheme}`}>
                    <FieldLabel label="Card Details" />
                    <CardElement
                      options={cardOptions}
                      onChange={event => {
                        setEnableSubmit(event.complete);
                      }}
                    />
                  </div>

                  <FormState
                    validateOnSubmit
                    initialValues={{ coupon: couponCode ?? "" }}
                    onSubmit={async couponFormData => {
                      if (!couponFormData.fields.coupon.value) {
                        return;
                      }

                      const result = await paymentsApi.validateCoupon(
                        authToken ?? teamsContext,
                        couponFormData.fields.coupon.value.trim()
                      );
                      if (isError(result)) {
                        const message =
                          result instanceof FetchError
                            ? (result.body?.error as string)
                            : result.message;
                        setGenericError(new Error(message));
                        setAppliedCouponCode(undefined);
                        return;
                      }
                      setGenericError(undefined);

                      setAppliedCouponCode(result);
                      setCouponCode(couponFormData.fields.coupon.value.trim());
                    }}
                  >
                    {couponForm => {
                      return (
                        <Flex vAlign="end" gap="gap.small">
                          <TextInputField
                            label="Promo Code"
                            fluid
                            field={couponForm.fields.coupon}
                            disabled={couponForm.submitting}
                            className={`stripe-input-field promo-field${classForDarkTheme}`}
                            onChange={(_event, value) => {
                              couponForm.fields.coupon.onChange(value?.value ?? "");
                              setCouponCode(undefined);
                              setAppliedCouponCode(undefined);
                            }}
                          />

                          <Flex.Item push>
                            <div>
                              <Button
                                className={couponCode ? "coupon-button-applied" : ""}
                                data-tname="redeem-coupon"
                                disabled={couponForm.submitting || !couponForm.fields.coupon.value}
                                loading={couponForm.submitting}
                                secondary
                                content={couponCode ? "Applied" : "Apply"}
                                icon={
                                  couponCode ? (
                                    <PollyIcon
                                      icon="correct-icon"
                                      ignoreTheme={true}
                                      inheritColor
                                    />
                                  ) : undefined
                                }
                                onClick={e => {
                                  e.preventDefault();
                                  couponForm.submit();
                                }}
                              />
                            </div>
                          </Flex.Item>
                        </Flex>
                      );
                    }}
                  </FormState>

                  {stripeError && (
                    <Alert content={stripeError.message} danger variables={{ error: true }} />
                  )}

                  {genericError && (
                    <Alert
                      content={genericError.message}
                      danger
                      variables={{ error: true }}
                      icon={<ExclamationTriangleIcon />}
                    />
                  )}

                  <Button
                    content={isTrial ? `Start Free Trial` : "Submit Payment"}
                    fluid
                    primary
                    disabled={submitting || !enableSubmit}
                    onClick={submit}
                    loading={submitting}
                    className="submit-button"
                  />

                  {enableSubmit && (
                    <Text
                      className="tax-notice"
                      content="*Tax will be charged for US customers where applicable"
                      size="small"
                      weight="semibold"
                    />
                  )}
                </>
              );
            }}
          </FormState>
        </Flex>
        <Flex column gap="gap.medium" className="payment-container payment-container-items">
          <Text
            content={selectedPlan.name}
            className={`payment-container-items__plan-name${classForDarkTheme}`}
          />
          {!isTrial && planType === "Pro" && (
            <>
              <Flex vAlign="center" gap="gap.small" styles={{ marginBottom: "5px !important" }}>
                <Text
                  content={`${noOfLicenses} License${noOfLicenses > 1 ? "s" : ""}`}
                  className={`payment-container-items__billing-options${classForDarkTheme}`}
                  styles={{ fontWeight: "600", width: "80px" }}
                />
                <Button
                  iconOnly
                  icon={<PollyIcon icon="minus-icon" size="small" />}
                  size="small"
                  disabled={noOfLicenses <= 1}
                  onClick={() => {
                    setNoOfLicenses(noOfLicenses - 1);
                  }}
                  styles={{ marginRight: "5px !important" }}
                />
                <Button
                  iconOnly
                  icon={<PollyIcon icon="plus-icon" size="small" inheritColor />}
                  size="small"
                  primary
                  onClick={() => {
                    setNoOfLicenses(noOfLicenses + 1);
                  }}
                />
              </Flex>
              <Text
                className={`payment-container-items__subtitle${classForDarkTheme}`}
                styles={{ marginBottom: "0px !important" }}
              >
                <>{noOfLicenses > 1 ? <>Add / remove licenses</> : <>Add more licenses</>}</>
              </Text>
            </>
          )}
          {appliedCouponCode && totalPrice && (
            <Flex gap="gap.small" className="payment-container-items__promo-container">
              <Text
                content={`- $${(totalPrice * (appliedCouponCode.percentOff / 100)).toFixed(
                  1
                )} with Promo`}
                className={`payment-container-items__promo-discount${classForDarkTheme}`}
              />
              <PollyIcon icon="correct-icon" ignoreTheme={true} inheritColor />
            </Flex>
          )}
          <Flex column gap="gap.small" styles={{ marginTop: "30px" }}>
            <Flex vAlign="start" className="payment-container-items__price-container">
              <Text
                content="$"
                className={`payment-container-items__dollar-symbol${classForDarkTheme}`}
              />
              <Text align="center" className={`payment-container-items__price${classForDarkTheme}`}>
                {!appliedCouponCode
                  ? totalPrice
                  : (totalPrice! - totalPrice! * (appliedCouponCode.percentOff / 100)).toFixed(1)}
                {enableSubmit && <sup className="asterik">*</sup>}
              </Text>
            </Flex>
            <Text
              content={isTrial ? "Total charged when trial expires" : "Total charged now"}
              className={`payment-container-items__subtitle${classForDarkTheme}`}
            />
            <Flex styles={{ marginTop: "25px", marginBottom: "0" }}>
              <Text
                content={
                  <>
                    The {selectedPlan.name} plan renews every {renewalFrequencyText}.{" "}
                    {!isTrial ? (
                      <>
                        Your next billing date is <b>{nextPaymentDate}</b>
                      </>
                    ) : (
                      <></>
                    )}
                  </>
                }
                className={`payment-container-items__subtitle${classForDarkTheme}`}
              />
            </Flex>
          </Flex>

          {isTrial && (
            <Flex.Item push>
              <Flex className="trial-disclaimer-container">
                <Text
                  content={`You can cancel your free trial at any time by visiting your Account Settings. You will automatically be charged at the end of your trial period on ${moment()
                    .add(user.featureFlags.trialDays, "days")
                    .format("LL")}`}
                  className="trial-disclaimer-container__content"
                />
              </Flex>
            </Flex.Item>
          )}
        </Flex>
      </Flex>

      <Flex fill className={`payment-footer${classForDarkTheme}`}>
        <Flex className="payment-footer__faqs" gap="gap.small">
          <Button
            className="contact-us-link__button"
            text
            onClick={e => {
              e.preventDefault();
              window.open(
                "https://www.polly.ai/help/microsoft-teams/subscription-management-faqs",
                "_blank"
              );
            }}
            content={
              <PollyIcon
                icon="q-info-icon"
                primary
                ignoreTheme={true}
                label={<Text weight="regular" content={"Billing Help FAQ"} />}
              />
            }
          />
        </Flex>
        <Flex className="payment-footer__contact-us">
          <ContactUsLink redirectToWebsite user={user} label="Contact Us" />
        </Flex>
      </Flex>
    </Flex>
  );
};
